/**
 * Is this page showing everything? — the shared truncation helpers.
 *
 * Consolidated 2026-08-16 from three independent copies that four parallel
 * agents each invented for the same problem (`views/devops/findTotal.ts`,
 * `views/infrastructure/findTotal.ts`, `views/meetingIntelligence/loadCap.ts`).
 * The two findTotal copies were byte-identical in code and differed only in
 * which caveat each had documented — which is precisely the duplication that
 * lets one copy get a fix the others never see.
 *
 * ── readFindTotal: WHY NOT `serviceStore.pagination.total` ──────────────────
 *
 * ServiceStore does carry a `pagination.total`, and it is the obvious thing to
 * reach for — but it is only written inside `if (this.pagination.enabled)`, and
 * the `{ data }` branch RETURNS EARLY whenever `data.length > 0`. So for a
 * store that never called `enablePagination()` it stays 0 forever, and even for
 * one that did, it is skipped on exactly the responses that carry rows. A
 * "total" that is 0 whenever there IS data reads as "the server has nothing" —
 * worse than no total at all. The find RESPONSE is returned verbatim, so the
 * envelope's own `total` is available and correct; read it there.
 *
 * Returns null — never 0, never the row count — when no trustworthy total
 * exists, so a caller can say nothing rather than assert something false.
 *
 * TWO SERVICE-SIDE CAVEATS, both about the service and not this helper:
 *   - The five Azure PROXY services (`devops-projects`/`-repos`/`-pipelines`/
 *     `-releases`/`-work-items`) return a bare array from a live REST call, so
 *     this returns null for them BY DESIGN. Their truncation is upstream in
 *     Azure's own paging and is disclosed by the boards' "first N" captions.
 *   - A service whose `total` is computed AFTER its limit (`instance-drift`
 *     returns `{ total: data.length }`) reports a total that can never disagree
 *     with the row count, so no caption derived from it could ever fire. See
 *     InstanceDriftStore.load for how that case is handled instead.
 *
 * ── the cap helpers ────────────────────────────────────────────────────────
 *
 * When a service reports no total at all, the only available signal is "the
 * read came back full", i.e. `returned >= requested`. That is a LOWER BOUND,
 * not a count, and the helpers below keep it labelled as one (`500+`) rather
 * than printing a saturated number as though it were exact.
 */

/** The response shapes a ServiceStore `find()` can resolve to. */
type FindEnvelope =
  | { total?: unknown; data?: unknown }
  | { result?: { total?: unknown; data?: unknown } }
  | unknown[]
  | null
  | undefined;

/**
 * The server-reported row total for a find, or null when the response does not
 * report one.
 *
 * NOTE this is the total BEFORE `$limit`, which is what makes a "Showing N of
 * M" caption meaningful. A service whose `total` is computed AFTER its limit
 * (the `instance-drift` service is one — it returns `{ total: data.length }`)
 * reports a total that can never disagree with the row count, so no caption
 * derived from it could ever fire. That is a property of the service, not of
 * this helper: see InstanceDriftStore.load for how that case is handled.
 */
export function readFindTotal(response: FindEnvelope): number | null {
  if (!response || Array.isArray(response)) return null;
  const envelope = response as {
    total?: unknown;
    result?: { total?: unknown };
  };
  const total = envelope.total ?? envelope.result?.total;
  return typeof total === "number" && Number.isFinite(total) ? total : null;
}

export const MEETING_INTEL_SERVER_CAP = 500;

/**
 * `meeting-mappings` (the Mapping Queue) is stricter still: its find hardcodes
 * `.limit(200)` and reads no query at all, so the client cannot even ask for a
 * different page size. 200 is stated here so the queue can recognise its own
 * cap; it is a mirror of a server constant, never a request.
 */
export const MAPPING_QUEUE_SERVER_CAP = 200;

/** What one list load asked for and what it actually got back. */
export interface LoadCap {
  /** The `$limit` the store requested. */
  requested: number;
  /** Rows the last successful find returned; null before the first load. */
  returned: number | null;
}

/**
 * Row count of a ServiceStore `find()` result, across every response shape the
 * store supports: a bare array (what these services return), a `{ data }`
 * envelope, or a `{ result: { data } }` envelope.
 *
 * Returns **null** when the response carries no recognisable row list — a
 * swallowed transport error resolves `undefined`, and treating that as "0 rows"
 * would clear a previously recorded cap and quietly re-hide the truncation.
 */
export function rowsReturned(response: unknown): number | null {
  if (Array.isArray(response)) return response.length;
  if (!response || typeof response !== "object") return null;

  const record = response as {
    data?: unknown;
    result?: { data?: unknown };
  };
  if (Array.isArray(record.data)) return record.data.length;
  if (Array.isArray(record.result?.data)) return record.result.data.length;
  return null;
}

/**
 * True when the last load came back at (or above) the page size it asked for.
 * The server clamps rather than errors, so this is the only truncation signal
 * available — and it is deliberately conservative in the safe direction: a
 * collection whose size is exactly the cap reports as capped, which overstates
 * doubt rather than understating it.
 */
export function isAtCap(cap: LoadCap): boolean {
  return cap.returned != null && cap.returned >= cap.requested;
}

/**
 * A count that may be a floor rather than a total, rendered honestly: `500+`
 * when the underlying set is capped, plain otherwise.
 */
export function lowerBound(value: number, capped: boolean): string | number {
  return capped ? `${value}+` : value;
}

/**
 * The DataIntegrity-style "Showing N of M" line (see
 * `views/admin/DataIntegrity/DataIntegrity.Body.tsx`), for the case where
 * client-side filters hide some of the loaded rows. `null` when it would say
 * nothing.
 */
export function showingCaption(
  shown: number,
  loaded: number,
  noun: string
): string | null {
  if (loaded <= 0 || shown >= loaded) return null;
  return `Showing ${shown} of ${loaded} loaded ${noun}`;
}

/**
 * The truncation warning: what was loaded, and what the numbers above it do and
 * do not cover. `null` when the set is not at the cap.
 *
 * `countsShown` (default true) appends the clause about the summary cards; pass
 * false on a surface that renders no counts, so the sentence stays true.
 */
export function capWarning(
  cap: LoadCap,
  noun: string,
  countsShown = true
): string | null {
  if (!isAtCap(cap)) return null;
  const base =
    `Loaded the ${cap.requested} most recent ${noun} — the server returns no more ` +
    `in one page and supports no paging here, so older ${noun} are not included`;
  return countsShown
    ? `${base} and every count on this screen is a lower bound.`
    : `${base}.`;
}

/**
 * Both captions, joined — the single line a list body renders under its table.
 *
 * `countsShown` is forwarded to {@link capWarning}: pass **false** on a surface
 * that renders no summary counts, or the line ends by warning about "every
 * count on this screen" when the screen has none. The Mapping Queue is exactly
 * that surface, and lost the flag when its caption moved into the store.
 */
export function listCaption(
  shown: number,
  loaded: number,
  cap: LoadCap,
  noun: string,
  countsShown = true
): string | null {
  const parts = [
    showingCaption(shown, loaded, noun),
    capWarning(cap, noun, countsShown),
  ].filter((part): part is string => part !== null);
  return parts.length ? parts.join(" · ") : null;
}
